import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Table, Modal, message, Input, Select, Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getTeamById,
  addTeamMember,
  removeTeamMember,
  type Team,
  TEAM_MEMBER_ROLES,
} from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';
import CommentPanel from './CommentPanel';

const { Title } = Typography;

export default function TeamDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('member');

  const canWrite = hasPermission('collaboration', 'write');

  const loadTeam = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getTeamById(id);
      if (res.data?.success && res.data.data) {
        setTeam(res.data.data);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const isAdminOrOwner =
    team?.owner_id === user?.user_id ||
    team?.members.some(
      (m) => m.user_id === user?.user_id && (m.role === 'owner' || m.role === 'admin')
    );

  const handleAddMember = async () => {
    if (!id || !addUserId.trim()) return;
    try {
      await addTeamMember(id, { user_id: addUserId.trim(), role: addRole });
      message.success(t('collaboration.team.add_member_success'));
      setAddUserId('');
      setAddRole('member');
      loadTeam();
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleRemoveMember = (userId: string) => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('collaboration.team.remove_member_confirm'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await removeTeamMember(id, userId);
          message.success(t('collaboration.team.remove_member_success'));
          loadTeam();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading || !team) return null;

  const memberColumns = [
    {
      title: t('collaboration.team.field.member_user_id'),
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: t('collaboration.team.field.member_role'),
      dataIndex: 'role',
      key: 'role',
      render: (v: string) => (
        <Tag color={v === 'owner' ? 'gold' : v === 'admin' ? 'blue' : 'default'}>
          {t(`collaboration.team.role_options.${v}`, v)}
        </Tag>
      ),
    },
    {
      title: t('collaboration.team.field.joined_at'),
      dataIndex: 'joined_at',
      key: 'joined_at',
      render: (v: string) => v ? new Date(v).toLocaleString() : '-',
    },
    ...(canWrite && isAdminOrOwner
      ? [
          {
            title: t('app.actions'),
            key: 'actions',
            render: (_: unknown, record: any) =>
              record.user_id !== team.owner_id ? (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveMember(record.user_id)}
                />
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/collaboration/teams')}>
          {t('app.back')}
        </Button>
      </Space>

      <Title level={4}>{team.name}</Title>

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('collaboration.team.field.name')}>{team.name}</Descriptions.Item>
        <Descriptions.Item label={t('collaboration.team.field.owner')}>{team.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('collaboration.team.field.description')} span={2}>
          {team.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('collaboration.team.field.visibility')}>
          <Tag color={team.visibility === 'public' ? 'green' : 'default'}>
            {t(`collaboration.team.visibility_options.${team.visibility}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('collaboration.team.field.is_active')}>
          <Tag color={team.is_active ? 'green' : 'red'}>
            {team.is_active ? t('app.yes') : t('app.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('collaboration.team.field.tags')} span={2}>
          {team.tags?.map((tag) => <Tag key={tag}>{tag}</Tag>) ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.created_at')}>
          {new Date(team.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.updated_at')}>
          {new Date(team.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      <Title level={5}>{t('collaboration.team.field.members')} ({team.members.length})</Title>

      {canWrite && isAdminOrOwner && (
        <Space style={{ marginBottom: 12 }}>
          <Input
            placeholder={t('collaboration.team.field.member_user_id')}
            value={addUserId}
            onChange={(e) => setAddUserId(e.target.value)}
            style={{ width: 200 }}
          />
          <Select
            value={addRole}
            onChange={setAddRole}
            style={{ width: 120 }}
            options={TEAM_MEMBER_ROLES.filter((r) => r !== 'owner').map((r) => ({
              value: r,
              label: t(`collaboration.team.role_options.${r}`),
            }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMember}
            disabled={!addUserId.trim()}
          >
            {t('collaboration.team.add_member')}
          </Button>
        </Space>
      )}

      <Table
        dataSource={team.members}
        columns={memberColumns}
        rowKey="user_id"
        pagination={false}
        style={{ marginBottom: 24 }}
      />

      <CommentPanel entityType="team" entityId={team.id} />
    </div>
  );
}
